import React from 'react';
import {
  HStack,
  VStack,
  Button,
  Text,
  Select,
  IconButton,
  useColorModeValue,
  Card,
  CardBody,
  useBreakpointValue,
} from '@chakra-ui/react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from 'react-icons/fi';

interface LoginRequestPaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onPageChange: (page: number, pageSize?: number) => void;
  loading?: boolean;
}

const LoginRequestPagination: React.FC<LoginRequestPaginationProps> = ({
  currentPage,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  loading = false,
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  const isMobile = useBreakpointValue({ base: true, md: false });

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const pageSizeOptions = [
    { value: 10, label: '10 per page' },
    { value: 25, label: '25 per page' },
    { value: 50, label: '50 per page' },
    { value: 100, label: '100 per page' },
  ];

  const handlePageSizeChange = (newPageSize: number) => {
    // Calculate which page to show to keep similar items visible
    const currentStartItem = (currentPage - 1) * pageSize;
    const newPage = Math.floor(currentStartItem / newPageSize) + 1;
    onPageChange(Math.max(1, newPage), newPageSize);
  };

  const generatePageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalItems === 0) {
    return null;
  }

  if (isMobile) {
    return (
      <Card bg={cardBg} border="1px solid" borderColor={borderColor} mt={4}>
        <CardBody>
          <VStack spacing={4}>
            <Text fontSize="sm" color={textColor} textAlign="center">
              Showing {startItem}-{endItem} of {totalItems.toLocaleString()} results
            </Text>

            <HStack spacing={2} justify="center">
              <IconButton
                aria-label="First page"
                icon={<FiChevronsLeft />}
                size="sm"
                isDisabled={currentPage <= 1 || loading}
                onClick={() => onPageChange(1)}
              />
              <IconButton
                aria-label="Previous page"
                icon={<FiChevronLeft />}
                size="sm"
                isDisabled={currentPage <= 1 || loading}
                onClick={() => onPageChange(currentPage - 1)}
              />
              
              <Text fontSize="sm" minW="100px" textAlign="center">
                Page {currentPage} of {totalPages}
              </Text>
              
              <IconButton
                aria-label="Next page"
                icon={<FiChevronRight />}
                size="sm"
                isDisabled={currentPage >= totalPages || loading}
                onClick={() => onPageChange(currentPage + 1)}
              />
              <IconButton
                aria-label="Last page"
                icon={<FiChevronsRight />}
                size="sm"
                isDisabled={currentPage >= totalPages || loading}
                onClick={() => onPageChange(totalPages)}
              />
            </HStack>

            <Select
              size="sm"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              maxW="150px"
            >
              {pageSizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor} mt={6}>
      <CardBody>
        <HStack justify="space-between" align="center">
          {/* Items info */}
          <VStack align="start" spacing={1}>
            <Text fontSize="sm" color={textColor}>
              Showing {startItem.toLocaleString()}-{endItem.toLocaleString()} of {totalItems.toLocaleString()} results
            </Text>
            <Select
              size="sm"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              maxW="150px"
            >
              {pageSizeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </VStack>

          {/* Page navigation */}
          <HStack spacing={1}>
            {/* First page */}
            <IconButton
              aria-label="First page"
              icon={<FiChevronsLeft />}
              size="sm"
              variant="outline"
              isDisabled={currentPage <= 1 || loading}
              onClick={() => onPageChange(1)}
            />

            {/* Previous page */}
            <IconButton
              aria-label="Previous page"
              icon={<FiChevronLeft />}
              size="sm"
              variant="outline"
              isDisabled={currentPage <= 1 || loading}
              onClick={() => onPageChange(currentPage - 1)}
            />

            {/* Page numbers */}
            {generatePageNumbers().map((page, index) => {
              if (page === '...') {
                return (
                  <Text key={`dots-${currentPage}-${index}`} px={2} fontSize="sm" color={textColor}>
                    ...
                  </Text>
                );
              }

              const pageNumber = page as number;
              return (
                <Button
                  key={pageNumber}
                  size="sm"
                  variant={currentPage === pageNumber ? 'solid' : 'outline'}
                  colorScheme={currentPage === pageNumber ? 'blue' : 'gray'}
                  isDisabled={loading}
                  onClick={() => onPageChange(pageNumber)}
                  minW="40px"
                >
                  {pageNumber}
                </Button>
              );
            })}

            {/* Next page */}
            <IconButton
              aria-label="Next page"
              icon={<FiChevronRight />}
              size="sm"
              variant="outline"
              isDisabled={currentPage >= totalPages || loading}
              onClick={() => onPageChange(currentPage + 1)}
            />

            {/* Last page */}
            <IconButton
              aria-label="Last page"
              icon={<FiChevronsRight />}
              size="sm"
              variant="outline"
              isDisabled={currentPage >= totalPages || loading}
              onClick={() => onPageChange(totalPages)}
            />
          </HStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default LoginRequestPagination;
